import { z } from "zod";
import { isCountryFieldValue } from "../countries.ts";
import {
  EntityIdSchema,
  EntityTypeSchema,
  IsoDate,
  IsoDateTime,
  LocalIdSchema,
  jsonColumn,
} from "./common.ts";
import type { FieldDef } from "./fieldDef.ts";

/** Any value a custom field can hold. The concrete shape is decided by its type. */
export const FieldValueDataSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

export type FieldValueData = z.infer<typeof FieldValueDataSchema>;

export const FieldValueSchema = z.object({
  entity_type: EntityTypeSchema,
  entity_id: EntityIdSchema,
  field_id: LocalIdSchema,
  value: jsonColumn(FieldValueDataSchema).default(null),
  updated_at: IsoDateTime,
});

export type FieldValue = z.infer<typeof FieldValueSchema>;

export const FieldValueWriteSchema = z.object({
  field_id: LocalIdSchema,
  /** `null` clears the value; the row is deleted rather than stored empty. */
  value: FieldValueDataSchema,
});

export type FieldValueWrite = z.infer<typeof FieldValueWriteSchema>;

/** Bulk edit: same field, same value, many entities. One row per entity. */
export const FieldValueBulkWriteSchema = z.object({
  entity_type: EntityTypeSchema,
  entity_ids: z.array(EntityIdSchema).min(1).max(10000),
  field_id: LocalIdSchema,
  value: FieldValueDataSchema,
});

/**
 * The schema a value must satisfy for a given field definition. Built at write
 * time because the rules live in the row, not in the type system — a `select`
 * only accepts ids its own `config.options` declares.
 */
export function valueSchemaFor(def: FieldDef): z.ZodType<FieldValueData> {
  const optionIds = (def.config?.options ?? []).map((o) => o.id);

  switch (def.type) {
    case "text":
    case "longtext":
      return z.string().max(def.type === "longtext" ? 100000 : 2000);

    case "number": {
      let schema = z.number().finite();
      if (def.config?.min !== undefined) schema = schema.min(def.config.min);
      if (def.config?.max !== undefined) schema = schema.max(def.config.max);
      return schema;
    }

    case "date":
      return IsoDate;

    case "url":
      return z.string().url().max(2000);

    case "checkbox":
      return z.boolean();

    case "select":
      return optionIds.length > 0
        ? z.enum(optionIds as [string, ...string[]])
        : z.string().max(200);

    case "multiselect":
      return optionIds.length > 0
        ? z.array(z.enum(optionIds as [string, ...string[]])).max(200)
        : z.array(z.string().max(200)).max(200);

    case "country":
      return z
        .string()
        .refine(isCountryFieldValue, {
          message: "Must be an ISO 3166-1 alpha-2 country code or OTHER",
        });
  }
}
